/**
 * Megamenu
 *
 * WCAG 2.1 AA-compliant megamenu with smooth slide transitions.
 * - Desktop: hover to open (with delay), click still works
 * - Mobile/touch: click/tap to open
 * - Enter / Space toggles the panel
 * - Escape closes the open panel and returns focus to trigger
 * - Tab moves through focusable items inside the panel, then out
 * - Clicking outside closes the open panel
 * - Respects prefers-reduced-motion
 */

const HOVER_OPEN_DELAY = 200; // ms — delay before opening on hover
const HOVER_CLOSE_DELAY = 300; // ms — grace period before closing on mouseleave

/**
 * Detect touch-only devices (no fine pointer = likely touch).
 */
const isTouchDevice = () =>
  window.matchMedia("(pointer: coarse)").matches &&
  !window.matchMedia("(pointer: fine)").matches;

/**
 * Cancel any pending close-transition cleanup attached to a panel.
 * Without this, a stale transitionend from a prior closePanel can fire
 * during a subsequent open transition and re-hide the panel.
 */
const cancelPendingClose = (panel) => {
  if (panel._pendingClose) {
    panel.removeEventListener("transitionend", panel._pendingClose);
    panel._pendingClose = null;
  }
};

/**
 * Show a panel with fade/slide transition.
 */
const openPanel = (trigger, panel) => {
  cancelPendingClose(panel);
  trigger.setAttribute("aria-expanded", "true");
  panel.hidden = false;
  // Force reflow so the transition starts from the hidden state.
  void panel.offsetHeight;
  panel.classList.add("is-open");
};

/**
 * Hide a panel with fade/slide transition.
 */
const closePanel = (trigger, panel, restoreFocus = false) => {
  cancelPendingClose(panel);
  trigger.setAttribute("aria-expanded", "false");
  panel.classList.remove("is-open");

  const onEnd = (e) => {
    if (e.target !== panel) return;
    panel.removeEventListener("transitionend", onEnd);
    panel._pendingClose = null;
    panel.hidden = true;
  };
  panel._pendingClose = onEnd;
  panel.addEventListener("transitionend", onEnd);

  if (restoreFocus) trigger.focus();
};

/**
 * Get all focusable elements inside an element.
 */
const getFocusable = (el) => [
  ...el.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  ),
];

const initMegamenus = () => {
  const triggers = document.querySelectorAll(".megamenu-trigger");
  if (!triggers.length) return;

  // Build a map of trigger → panel pairs.
  const menus = [];

  triggers.forEach((trigger) => {
    const panelId = trigger.getAttribute("aria-controls");
    const panel = document.getElementById(panelId);
    if (!panel) return;
    menus.push({ trigger, panel });
  });

  /**
   * Close all panels except the one to keep open.
   */
  const closeAll = (except = null) => {
    menus.forEach(({ trigger, panel }) => {
      if (
        panel !== except &&
        trigger.getAttribute("aria-expanded") === "true"
      ) {
        closePanel(trigger, panel);
      }
    });
  };

  /**
   * Handle trigger click — toggle this panel, close others.
   * When opened via keyboard (Enter/Space), move focus into the panel.
   */
  menus.forEach(({ trigger, panel }) => {
    trigger.addEventListener("click", (e) => {
      const isExpanded = trigger.getAttribute("aria-expanded") === "true";

      if (isExpanded) {
        closePanel(trigger, panel);
      } else {
        closeAll(panel);
        openPanel(trigger, panel);

        // If activated via keyboard, move focus into the panel.
        // detail === 0 means no mouse click (keyboard or programmatic).
        if (e.detail === 0) {
          const focusable = getFocusable(panel);
          if (focusable.length) focusable[0].focus();
        }
      }
    });
  });

  /**
   * When a trigger receives focus, close any other open panel.
   * Prevents stale panels lingering while tabbing across triggers.
   */
  menus.forEach(({ trigger }) => {
    trigger.addEventListener("focus", () => {
      closeAll();
    });
  });

  /**
   * Hover to open on desktop (non-touch devices).
   * Uses a short delay to prevent accidental triggers and a grace
   * period on mouseleave so the cursor can travel to the panel.
   */
  menus.forEach(({ trigger, panel }) => {
    let openTimeout = null;
    let closeTimeout = null;

    const clearTimers = () => {
      clearTimeout(openTimeout);
      clearTimeout(closeTimeout);
    };

    const scheduleOpen = () => {
      if (isTouchDevice()) return;
      clearTimers();
      openTimeout = setTimeout(() => {
        if (trigger.getAttribute("aria-expanded") !== "true") {
          closeAll(panel);
          openPanel(trigger, panel);
        }
      }, HOVER_OPEN_DELAY);
    };

    const scheduleClose = () => {
      if (isTouchDevice()) return;
      clearTimers();
      closeTimeout = setTimeout(() => {
        if (trigger.getAttribute("aria-expanded") === "true") {
          closePanel(trigger, panel);
        }
      }, HOVER_CLOSE_DELAY);
    };

    // Hovering the trigger or the panel keeps it open.
    trigger.addEventListener("mouseenter", scheduleOpen);
    trigger.addEventListener("mouseleave", scheduleClose);
    panel.addEventListener("mouseenter", () => {
      if (!isTouchDevice()) clearTimers();
    });
    panel.addEventListener("mouseleave", scheduleClose);
  });

  /**
   * Escape key — close the open panel and restore focus.
   */
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;

    const openMenu = menus.find(
      ({ trigger }) => trigger.getAttribute("aria-expanded") === "true",
    );

    if (openMenu) {
      e.preventDefault();
      closePanel(openMenu.trigger, openMenu.panel, true);
    }
  });

  /**
   * Click outside — close any open panel.
   */
  document.addEventListener("click", (e) => {
    const openMenu = menus.find(
      ({ trigger }) => trigger.getAttribute("aria-expanded") === "true",
    );
    if (!openMenu) return;

    const clickedInsidePanel = openMenu.panel.contains(e.target);
    const clickedTrigger = menus.some(({ trigger }) =>
      trigger.contains(e.target),
    );

    if (!clickedInsidePanel && !clickedTrigger) {
      closePanel(openMenu.trigger, openMenu.panel);
    }
  });

  /**
   * Tab navigation within panels.
   *
   * Panels are siblings after the <nav> in markup, so natural Tab order
   * doesn't return to the nav triggers. We fix this with JS:
   *
   * - Tab past last item → close panel, focus next trigger (or next
   *   focusable after all triggers if this is the last menu).
   * - Shift+Tab from first item → close panel, return to trigger.
   */
  menus.forEach(({ trigger, panel }, index) => {
    panel.addEventListener("keydown", (e) => {
      if (e.key !== "Tab") return;

      const focusable = getFocusable(panel);
      if (!focusable.length) return;

      if (
        !e.shiftKey &&
        document.activeElement === focusable[focusable.length - 1]
      ) {
        e.preventDefault();
        closePanel(trigger, panel);

        // Move to the next trigger, or let focus leave the nav naturally.
        const nextMenu = menus[index + 1];
        if (nextMenu) {
          nextMenu.trigger.focus();
        } else {
          // Last menu — find the next focusable element after all nav triggers.
          // Focus the "Get a Card" button or whatever follows in the nav.
          const allNavFocusable = getFocusable(
            trigger.closest(".site-header-main-inner") || document,
          );
          const triggerIndex = allNavFocusable.indexOf(trigger);
          const next = allNavFocusable[triggerIndex + 1];
          if (next) {
            next.focus();
          } else {
            trigger.focus();
          }
        }
      } else if (e.shiftKey && document.activeElement === focusable[0]) {
        e.preventDefault();
        closePanel(trigger, panel, true);
      }
    });
  });
};

document.addEventListener("DOMContentLoaded", initMegamenus);
